import { 
  ProcessActivityApi, 
  ProcessInstanceDetailData,
  ProcessVariable
} from '../types/process';
import { 
  ProcessInstanceTimeline, 
  TimelineEvent, 
  TimelineEventType, 
  TimelineCategory, 
  EventStatus 
} from '../types/timeline';

/**
 * Converte atividades da API para eventos da timeline
 */
export function convertActivitiesToTimelineEvents(activities: ProcessActivityApi[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  activities.forEach((activity) => {
    // Determinar o tipo de evento baseado no activity_type
    let eventType: TimelineEventType;
    let category: TimelineCategory;

    switch (activity.activity_type) {
      case 'startEvent':
        eventType = TimelineEventType.PROCESS_START;
        category = TimelineCategory.PROCESS;
        break;
      case 'endEvent':
      case 'noneEndEvent':
        eventType = TimelineEventType.PROCESS_END;
        category = TimelineCategory.PROCESS;
        break;
      case 'userTask':
        eventType = TimelineEventType.TASK_CREATED;
        category = TimelineCategory.TASK;
        break;
      case 'serviceTask':
        eventType = TimelineEventType.EXTERNAL_TASK_CREATED;
        category = TimelineCategory.EXTERNAL_TASK;
        break;
      case 'scriptTask':
      case 'businessRuleTask':
      case 'task':
        eventType = TimelineEventType.ACTIVITY_START;
        category = TimelineCategory.ACTIVITY;
        break;
      case 'parallelGateway':
      case 'exclusiveGateway':
        eventType = TimelineEventType.GATEWAY_PASSED;
        category = TimelineCategory.GATEWAY;
        break;
      case 'boundaryTimer':
        eventType = TimelineEventType.ACTIVITY_START;
        category = TimelineCategory.ACTIVITY;
        break;
      default:
        eventType = TimelineEventType.ACTIVITY_START;
        category = TimelineCategory.ACTIVITY;
    }

    // Criar evento principal
    events.push({
      id: `activity_${activity.id}`,
      timestamp: activity.start_time,
      type: eventType,
      category: category,
      activityId: activity.activity_id,
      activityName: activity.activity_name || activity.activity_id,
      activityType: activity.activity_type,
      executionId: activity.execution_id,
      taskId: activity.task_id,
      duration: activity.duration_in_millis,
      status: activity.canceled ? EventStatus.CANCELLED : EventStatus.COMPLETED,
      details: {
        activityInstanceId: activity.id,
        parentActivityInstanceId: activity.parent_activity_instance_id,
        canceled: activity.canceled
      },
      metadata: {
        rootProcessInstanceId: activity.root_process_instance_id,
        level: 1
      }
    });

    // Para user tasks e service tasks, criar também evento de conclusão se tiver end_time
    if ((activity.activity_type === 'userTask' || activity.activity_type === 'serviceTask') && activity.end_time) {
      const completionEventType = activity.activity_type === 'userTask' 
        ? TimelineEventType.TASK_COMPLETED 
        : TimelineEventType.EXTERNAL_TASK_COMPLETED;

      events.push({
        id: `activity_completed_${activity.id}`,
        timestamp: activity.end_time,
        type: completionEventType,
        category: category,
        activityId: activity.activity_id,
        activityName: activity.activity_name || activity.activity_id,
        activityType: activity.activity_type,
        executionId: activity.execution_id,
        taskId: activity.task_id,
        duration: null,
        status: activity.canceled ? EventStatus.CANCELLED : EventStatus.COMPLETED,
        details: {
          activityInstanceId: activity.id,
          deleteReason: activity.canceled ? "canceled" : "completed"
        },
        metadata: {
          rootProcessInstanceId: activity.root_process_instance_id,
          level: 1,
          parentEventId: `activity_${activity.id}`
        }
      });
    }
  });

  return events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

/**
 * Cria eventos de variáveis correlacionadas com atividades
 */
export function createVariableEvents(
  activities: ProcessActivityApi[],
  variables: ProcessVariable[]
): TimelineEvent[] {
  const variableEvents: TimelineEvent[] = [];

  variables.forEach((variable) => {
    if (variable.activity_instance_id) {
      // Encontrar a atividade correspondente
      const activity = activities.find(a => a.id === variable.activity_instance_id);
      
      if (activity) {
        variableEvents.push({
          id: `variable_${variable.id}`,
          timestamp: variable.create_time,
          type: TimelineEventType.VARIABLE_UPDATE,
          category: TimelineCategory.VARIABLE,
          activityId: activity.activity_id,
          activityName: activity.activity_name || activity.activity_id,
          activityType: activity.activity_type,
          executionId: activity.execution_id,
          taskId: activity.task_id,
          duration: null,
          status: EventStatus.COMPLETED,
          details: {
            activityInstanceId: activity.id,
            variableName: variable.name,
            variableType: variable.type,
            variableValue: variable.value,
            initial: true
          },
          metadata: {
            rootProcessInstanceId: activity.root_process_instance_id,
            level: 2,
            parentEventId: `activity_${activity.id}`
          }
        });
      }
    }
  });

  return variableEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

/**
 * Cria uma timeline completa a partir dos dados da API
 */
export function createTimelineFromApiData(
  instance: ProcessInstanceDetailData,
  activities: ProcessActivityApi[],
  variables: ProcessVariable[] = []
): ProcessInstanceTimeline {
  const activityEvents = convertActivitiesToTimelineEvents(activities);
  const variableEvents = createVariableEvents(activities, variables);
  
  // Combinar todos os eventos e ordenar por timestamp
  const timelineEvents = [...activityEvents, ...variableEvents].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Calcular estatísticas dinamicamente
  const eventsByType = timelineEvents.reduce((acc, event) => {
    acc[event.type] = (acc[event.type] || 0) + 1;
    return acc;
  }, {} as Record<TimelineEventType, number>);

  const eventsByCategory = timelineEvents.reduce((acc, event) => {
    acc[event.category] = (acc[event.category] || 0) + 1;
    return acc;
  }, {} as Record<TimelineCategory, number>);

  // Encontrar atividade mais longa e mais curta
  const activitiesWithDuration = timelineEvents.filter(e => e.duration && e.duration > 0);
  
  const longestActivity = activitiesWithDuration.reduce((max, event) => 
    (event.duration || 0) > (max.duration || 0) ? event : max, 
    activitiesWithDuration[0] || { id: "", activityName: "", duration: 0 }
  );
  
  const shortestActivity = activitiesWithDuration.reduce((min, event) => 
    (event.duration || 0) < (min.duration || 0) ? event : min, 
    activitiesWithDuration[0] || { id: "", activityName: "", duration: 0 }
  );

  // Calcular critical path e bottlenecks
  const totalDuration = instance.end_time 
    ? new Date(instance.end_time).getTime() - new Date(instance.start_time).getTime()
    : 0;
    
  const mainBottleneck = longestActivity.duration ? {
    eventId: longestActivity.id,
    activityName: longestActivity.activityName || "",
    duration: longestActivity.duration,
    percentage: totalDuration > 0 ? Math.round((longestActivity.duration / totalDuration) * 100 * 100) / 100 : 0
  } : null;

  // Identificar eventos do critical path (início, gargalo principal, fim)
  const startEvent = timelineEvents.find(e => e.type === TimelineEventType.PROCESS_START);
  const endEvent = timelineEvents.find(e => e.type === TimelineEventType.PROCESS_END);
  const criticalPathEvents = [
    startEvent?.id,
    longestActivity.id,
    endEvent?.id
  ].filter(Boolean) as string[];

  return {
    processInstance: {
      id: instance.id,
      businessKey: instance.business_key || null,
      processDefinitionId: instance.process_definition_id,
      processDefinitionKey: instance.process_key,
      processDefinitionName: instance.process_name,
      processDefinitionVersion: 1, // TODO: obter da API quando disponível
      startTime: instance.start_time,
      endTime: instance.end_time || null,
      durationInMillis: totalDuration,
      state: instance.status,
      startUserId: null, // TODO: obter da API quando disponível
      deleteReason: null, // TODO: obter da API quando disponível
      tenantId: null // TODO: obter da API quando disponível
    },
    timeline: timelineEvents,
    statistics: {
      totalEvents: timelineEvents.length,
      totalDuration: totalDuration,
      eventsByType: eventsByType,
      eventsByCategory: eventsByCategory,
      parallelBranches: [],
      criticalPath: {
        totalDuration: totalDuration,
        events: criticalPathEvents,
        bottlenecks: mainBottleneck ? [mainBottleneck] : []
      },
      performanceMetrics: {
        averageActivityDuration: activitiesWithDuration.length > 0 
          ? activitiesWithDuration.reduce((sum, e) => sum + (e.duration || 0), 0) / activitiesWithDuration.length 
          : 0,
        longestActivity: {
          eventId: longestActivity.id,
          activityName: longestActivity.activityName || "",
          duration: longestActivity.duration || 0
        },
        shortestActivity: {
          eventId: shortestActivity.id,
          activityName: shortestActivity.activityName || "",
          duration: shortestActivity.duration || 0
        },
        waitingTime: 0, // TODO: calcular tempo de espera
        processingTime: totalDuration
      }
    }
  };
}
